import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface ISubjectOutline {
  id: number;
  sessionId: number;
  subjectId: number;
  substrandId: number;
  classroomId: number;
  schoolId: number;
  lessonsCount: number;
  created: string;
  modified: string;
}

export interface ISubjectOutlineFilters {
  page?: number;
  pageSize?: number;
  sessionId?: number;
  subjectId?: number;
  substrandId?: number;
  classroomId?: number;
}

export interface ISubjectOutlineApiResponse {
  data: ISubjectOutline[];
  meta: {
    page: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface ISubjectOutlineDetailResponse {
  data: ISubjectOutline;
  meta: {
    name: string;
  };
}

export const SubjectOutlinesApi = createApi({
  reducerPath: 'SubjectOutlinesApi',
  baseQuery,
  tagTypes: ['SubjectOutline'],
  endpoints: builder => ({
    getSubjectOutlines: builder.query<
      ISubjectOutlineApiResponse,
      ISubjectOutlineFilters
    >({
      query: filters => {
        const queryParams = new URLSearchParams();
        if (filters.page) queryParams.append('page', filters.page.toString());
        if (filters.pageSize)
          queryParams.append('pageSize', filters.pageSize.toString());
        if (filters.sessionId)
          queryParams.append('sessionId', filters.sessionId.toString());
        if (filters.subjectId)
          queryParams.append('subjectId', filters.subjectId.toString());
        if (filters.substrandId)
          queryParams.append('substrandId', filters.substrandId.toString());
        if (filters.classroomId)
          queryParams.append('classroomId', filters.classroomId.toString());

        return `/academics/subject-outlines?${queryParams.toString()}`;
      },
      providesTags: ['SubjectOutline'],
    }),

    getSubjectOutlineById: builder.query<ISubjectOutlineDetailResponse, number>(
      {
        query: id => `/academics/subject-outlines/${id}`,
        providesTags: ['SubjectOutline'],
      }
    ),

    createSubjectOutline: builder.mutation<
      ISubjectOutlineDetailResponse,
      Partial<ISubjectOutline>
    >({
      query: subjectOutlineData => ({
        url: '/academics/subject-outlines',
        method: 'POST',
        body: subjectOutlineData,
      }),
      invalidatesTags: ['SubjectOutline'],
    }),

    updateSubjectOutline: builder.mutation<
      ISubjectOutlineDetailResponse,
      { id: number; data: Partial<ISubjectOutline> }
    >({
      query: ({ id, data }) => ({
        url: `/academics/subject-outlines/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SubjectOutline'],
    }),

    deleteSubjectOutline: builder.mutation<void, number>({
      query: id => ({
        url: `/academics/subject-outlines/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubjectOutline'],
    }),
  }),
});

export const {
  useGetSubjectOutlinesQuery,
  useGetSubjectOutlineByIdQuery,
  useCreateSubjectOutlineMutation,
  useUpdateSubjectOutlineMutation,
  useDeleteSubjectOutlineMutation,
} = SubjectOutlinesApi;
